// src/App.js
import React, { useEffect } from "react"; // Import useEffect
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation, // <-- Import useLocation
} from "react-router-dom";
import { AuthProvider, useAuth } from "./useAuth";
import SignIn from "./SignIn";
import SignUp from "./SignUp";
import JobPostingsPage from "./pages/JobPostingsPage";
import Feedbacks from "./pages/FeedbackPage";
import CandidateListPage from "./pages/CandidateListPage";
import ContactSalesPage from "./pages/ContactSales"
import ApplicationsPage from "./pages/ApplicationsPage";
import HiringCostCalculator from "./pages/HiringCostCalculator";
import ContactPage from "./pages/ContactPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AffiliatePage from "./pages/AffiliatePage";
import JobListingsPage from "./pages/JobListingsPage";
import ThankYouPage from "./pages/ThankYouPage";
import Profile from "./pages/UserProfile";
// import TestPage from "./pages/test";
import AssessmentPage from "./pages/Assessments";
import AssessmentResultsPage from "./pages/AssessmentResults";
import Reviews from "./pages/Reviews";
import Pricing from "./pages/Pricing";
// import Pricing2 from "./pages/Pricing copy";
import SupportPage from "./pages/SupportPage";
import DocumentationPage from "./pages/DocumentationPage";
import GettingStartedGuide from "./pages/GettingStartedGuide";
import LicenseBuyingPage from "./pages/LicenseBuyingPage";
// import DashboardPage2 from "./pages/DashboardPage2";
import JobPostingDetailsPage from "./pages/JobPostingDetailsPage";
import DashboardPage from "./pages/DashboardPage";
import CreateAssessment from "./pages/CreateAssessment";
import UpdateAssessment from "./pages/UpdateAssessment";
import StripePaymentReturn from "./pages/StripePaymentReturn";
import NotFound from "./pages/NotFound";
import FAQPage from "./pages/FAQPage";
import { fetchUserByEmail } from "./utils/redux/userDataSlice";
import { useDispatch, useSelector } from "react-redux";
import { ToastProvider, useToast } from "./components/Alerts";
import CompanyEditPage from "./pages/CompanyEditPage";
import "react-tooltip/dist/react-tooltip.css";
import { Tooltip } from "react-tooltip";
import ProspectVerificationPage from "./pages/ProspectVerificationPage";

// import DialogflowMessenger from "./components/DialogflowMessenger";

// A component to bridge Redux state to the Toast context
const GlobalToastNotifier = () => {
  const { addToast } = useToast();
  const notification = useSelector((state) => state.notifications);

  useEffect(() => {
    if (notification?.message) {
      addToast({ message: notification.message, type: notification.type });
    }
    // We use notification.key to re-trigger the effect even if the message and type are the same
  }, [notification?.key, notification?.message, notification?.type, addToast]);
  return null; // This component does not render anything
};



// --- MODIFIED PrivateRoute ---
// eslint-disable-next-line react/prop-types
const PrivateRoute = ({ element }) => { // Use 'element' prop as per React Router v6 convention
  const dispatch = useDispatch();
  const { currentUser } = useAuth();
  const location = useLocation(); // <-- Get the current location

  // Use useEffect for side effects like dispatching user data fetch
  useEffect(() => {
    // Only dispatch if we have a logged-in user and their email
    if (currentUser?.email) {
       // console.log("PrivateRoute Effect: Fetching user data for", currentUser.email);
      dispatch(fetchUserByEmail(currentUser.email));
    }
  }, [dispatch, currentUser?.email]); // Dependencies: re-run if dispatch function or user email changes

  if (!currentUser) {
     // User not logged in, redirect to signin page
     // Pass the current location in state so SignIn page can redirect back
     // console.log(`PrivateRoute: Not logged in. Redirecting to /signin from ${location.pathname}`);
    return <Navigate to="/signin" state={{ from: location }} replace />; // <-- Pass location state
  }

  // User is logged in, render the requested element component
  // The useEffect above handles the data fetching side effect
   // console.log(`PrivateRoute: Logged in. Rendering element for ${location.pathname}`);
  return element;
};
// --- End of MODIFIED PrivateRoute ---

function App() {
  // const { t } = useTranslation();
  return (
    <AuthProvider>
      <Tooltip anchorSelect=".my-anchor-element" place="top" />
      <ToastProvider>
        <GlobalToastNotifier />
        <Router>
          <Routes>
            {/* --- Public Routes --- */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/feedbacks" element={<Feedbacks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/affiliate" element={<AffiliatePage />} />
            <Route path="/calculator" element={<HiringCostCalculator />} />
            <Route path="/democontact" element={<ContactPage />} />
            <Route path="/contact-sales" element={<ContactSalesPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} /> {/* Alias*/}
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/job_listings" element={<JobListingsPage />} />
            <Route path="/faq" element={<FAQPage />} />

             {/* Routes that might be public or have their own internal checks */}
            <Route path="/jobs/:jobID" element={<ApplicationsPage />} />
            <Route
                path="/assessments/:assessmentID/test/:testID"
                element={<AssessmentPage />} // This seems public or semi-public?
            />
            <Route
                path="/gettingstartedguide" // Often accessible right after signup/login
                element={<GettingStartedGuide />}
            />

            {/* --- Private Routes --- */}
            {/* Use the modified PrivateRoute component to wrap protected elements */}
            <Route path="/ProspectVerificationPage" element={<PrivateRoute element={<ProspectVerificationPage />} />} />
            <Route path="/kwami" element={<PrivateRoute element={<Reviews />} />} />
            <Route
              path="/checkout"
              element={<PrivateRoute element={<LicenseBuyingPage />} />}
            />
            <Route
                path="/stripe_payment_return"
                element={<PrivateRoute element={<StripePaymentReturn />} />} // Likely needs auth check internally or via webhook data
            />
            <Route
              path="/company-edit"
              element={<PrivateRoute element={<CompanyEditPage />} />}
            />
            <Route
              path="/dashboard"
              element={<PrivateRoute element={<DashboardPage />} />}
            />
            <Route
              path="/profile"
              element={<PrivateRoute element={<Profile />} />}
            />
            <Route
              path="/createAssessment"
              element={<PrivateRoute element={<CreateAssessment />} />}
            />
            <Route
              path="/updateAssessment/:id"
              element={<PrivateRoute element={<UpdateAssessment />} />}
            />
            <Route
              path="/job-postings"
              element={<PrivateRoute element={<JobPostingsPage />} />}
            />
             <Route
              path="/jobpostings" // Alias
              element={<PrivateRoute element={<JobPostingsPage />} />}
            />
            <Route
              path="/" // Root path often redirects to dashboard or job postings
              element={<PrivateRoute element={<DashboardPage />} />} // Or JobPostingsPage
            />
            <Route
              path="/jobpostings/:jobId/tenants/:tenantId"
              element={<PrivateRoute element={<JobPostingDetailsPage />} />}
            />
            <Route
              path="/candidates/:jobID/tenants/:tenantID"
              element={<PrivateRoute element={<CandidateListPage />} />}
            />
            <Route
              path="/assessmentresults/:assessmentID/test/:testID"
              element={<PrivateRoute element={<AssessmentResultsPage />} />}
            />

            {/* --- Not Found Route --- */}
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<NotFound />} /> {/* Catch all */}
          </Routes>
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
}

export default App;